import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type CreateBody =
  paths["/api/v1/contracts"]["post"]["requestBody"]["content"]["application/json"];
type RenewBody =
  paths["/api/v1/contracts/{contract_id}/renew"]["post"]["requestBody"]["content"]["application/json"];

export const contractKeys = {
  all: ["contracts"] as const,
  detail: (id: string) => ["contracts", "detail", id] as const,
};

export function useContracts() {
  return useQuery({
    queryKey: contractKeys.all,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/contracts", {});
      if (error || !data) throw new Error("Failed to load contracts");
      return data;
    },
  });
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: contractKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/contracts/{contract_id}", {
        params: { path: { contract_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load contract");
      return data;
    },
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBody) => {
      const { data, error } = await apiClient.POST("/api/v1/contracts", { body });
      if (error || !data) throw new Error("Failed to create contract");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contractKeys.all }),
  });
}

export function useRenewContract(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RenewBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/renew",
        { params: { path: { contract_id: id } }, body },
      );
      if (error || !data) throw new Error("Failed to create renewal");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contractKeys.all }),
  });
}

/** The contract's lifecycle transitions: activate · end · close · cancel. */
export function useContractLifecycle(id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: contractKeys.all });
    qc.invalidateQueries({ queryKey: contractKeys.detail(id) });
  };
  const path = { contract_id: id };

  const activate = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/activate",
        { params: { path } },
      );
      if (error || !data) throw new Error("Failed to activate contract");
      return data;
    },
    onSuccess: invalidate,
  });

  const end = useMutation({
    mutationFn: async (endDate: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/end",
        { params: { path }, body: { end_date: endDate } },
      );
      if (error || !data) throw new Error("Failed to end contract");
      return data;
    },
    onSuccess: invalidate,
  });

  const close = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/close",
        { params: { path } },
      );
      if (error || !data) throw new Error("Failed to close contract");
      return data;
    },
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/cancel",
        { params: { path } },
      );
      if (error || !data) throw new Error("Failed to cancel contract");
      return data;
    },
    onSuccess: invalidate,
  });

  return { activate, end, close, cancel };
}

/** Attach an existing guarantor to a draft contract (jointly & severally liable). */
export function useAddContractGuarantor(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      guarantor_id: string;
      guarantees_tenant_id?: string | null;
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/guarantors",
        { params: { path: { contract_id: contractId } }, body: vars },
      );
      if (error || !data) throw new Error("Failed to add guarantor");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.all });
      qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
    },
  });
}

/** Detach a guarantor from a draft contract. */
export function useRemoveContractGuarantor(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (guarantorId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/contracts/{contract_id}/guarantors/{guarantor_id}",
        {
          params: { path: { contract_id: contractId, guarantor_id: guarantorId } },
        },
      );
      if (error) throw new Error("Failed to remove guarantor");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.all });
      qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
    },
  });
}

export function useUpdateContract(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/contracts/{contract_id}",
        { params: { path: { contract_id: id } }, body: body as never },
      );
      if (error || !data) throw new Error("Failed to update contract");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contractKeys.all }),
  });
}
